import { Text } from '@/components/ui/text';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { getPressProps } from '@/util/pressProps';

interface StatementChipProps {
  /** 0-based index of the statement this chip belongs to. */
  statementIndex: number;
  /** The reference's footnote number, e.g. `1` for `[1]`. */
  number: number;
  /** How many references the statement carries, to tell them apart for a
   * screen reader ("Reference 2 of 3 for statement 1"). */
  totalForStatement: number;
  position: 'left' | 'right';
  /** Opens the reference. Omitted for a number with nothing to open yet, as
   * when it is the author's pending request, and the chip is then plain text. */
  onPress?: (number: number) => void;
}

/**
 * Label color for the `[N]` text-only chip. White on the right-side
 * dark green bubble, primary green on the left-side neutral bubble.
 */
export function numberedChipColorFor(
  position: 'left' | 'right',
  colors: ReturnType<typeof useColors>['colors']
): string {
  return position === 'right' ? colors.typography.white : colors.primary[600];
}

/**
 * The `[N]` footnote marker after an underlined statement, one per reference
 * the statement carries. A tap opens that reference in the reference sheet.
 */
export function StatementChip({
  statementIndex,
  number,
  totalForStatement,
  position,
  onPress,
}: StatementChipProps) {
  const { colors } = useColors();

  const labelText = `[${number}]`;

  const labelColor = numberedChipColorFor(position, colors);

  if (!onPress) {
    /**
     * Hidden from assistive tech so a screen reader doesn't read the
     * bracketed number as punctuation. The footer lists the reference.
     */
    return (
      <Text
        size="xs"
        className="font-bold"
        style={{ color: labelColor }}
        aria-hidden
        accessibilityElementsHidden>
        {labelText}
      </Text>
    );
  }

  const a11yLabel =
    totalForStatement > 1
      ? `Reference ${number} of ${totalForStatement} for statement ${statementIndex + 1}`
      : `Reference ${number} for statement ${statementIndex + 1}`;

  return (
    <Text
      size="xs"
      className="font-bold web:cursor-pointer"
      {...getPressProps(() => onPress(number), getAccessibleHitSlop(24))}
      style={{ color: labelColor }}
      {...getAccessibilityProps({
        label: a11yLabel,
        role: 'button',
        hint: 'Shows the full reference',
      })}>
      {labelText}
    </Text>
  );
}
